"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.lexProvider = void 0;
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
const client_lex_runtime_v2_1 = require("@aws-sdk/client-lex-runtime-v2");
const utils_1 = require("@aws-amplify/core/internals/utils");
const core_1 = require("@aws-amplify/core");
const utils_2 = require("../utils");
const logger = new core_1.ConsoleLogger('AWSLexV2Provider');
class AWSLexV2Provider {
    constructor() {
        // Per-context onComplete callback registry (context isolation). See
        // `createPerContextCallbackRegistry` for the full documentation, including
        // the re-configure semantics (`Amplify.configure()` publishes a NEW frozen
        // global context object, so callbacks registered against a prior global
        // context are intentionally not carried over).
        this._botsCompleteCallbackByCtx = (0, utils_2.createPerContextCallbackRegistry)();
        this.defaultSessionId = (0, utils_1.amplifyUuid)();
    }
    /**
     * Send a message to a bot
     * @async
     * @param {AmplifyContext} ctx - Amplify context used to resolve credentials
     * @param {AWSLexV2ProviderOption} botConfig - Bot configuration for sending the message
     * @param {string | InteractionsMessage} message - message to send to the bot
     * @return {Promise<InteractionsResponse>} A promise resolves to the response from the bot
     */
    async sendMessage(ctx, botConfig, message) {
        // check if credentials are present
        let session;
        try {
            session = await ctx.fetchAuthSession();
        }
        catch (error) {
            return Promise.reject(new Error('No credentials'));
        }
        const { region, aliasId, localeId, botId } = botConfig;
        const client = new client_lex_runtime_v2_1.LexRuntimeV2Client({
            region,
            credentials: session.credentials,
            customUserAgent: (0, utils_1.getAmplifyUserAgentObject)(),
        });
        let response;
        // common base params for all requests
        const reqBaseParams = {
            botAliasId: aliasId,
            botId,
            localeId,
            sessionId: session.identityId ?? this.defaultSessionId,
        };
        if (typeof message === 'string') {
            response = await this._handleRecognizeTextCommand(ctx, botConfig, message, reqBaseParams, client);
        }
        else {
            response = await this._handleRecognizeUtteranceCommand(ctx, botConfig, message, reqBaseParams, client);
        }
        return response;
    }
    /**
     * Attach a onComplete callback function to a bot.
     * The callback is called once the bot's intent is fulfilled
     * @param {AmplifyContext} ctx - Amplify context the callback is scoped to
     * @param {AWSLexV2ProviderOption} botConfig - Bot configuration to attach the onComplete callback
     * @param {InteractionsOnCompleteCallback} callback - called when Intent Fulfilled
     */
    onComplete(ctx, { name }, callback) {
        this._botsCompleteCallbackByCtx.set(ctx, name, callback);
    }
    /**
     * call onComplete callback for a bot if configured
     */
    _reportBotStatus(ctx, data, { name }) {
        const sessionState = data?.sessionState;
        // Check if state is fulfilled to resolve onFullfilment promise
        logger.debug('postContent state', sessionState?.intent?.state);
        // Only fire callbacks registered against this exact context (isolation).
        const callback = this._botsCompleteCallbackByCtx.get(ctx, name);
        if (!callback) {
            return;
        }
        switch (sessionState?.intent?.state) {
            case client_lex_runtime_v2_1.IntentState.READY_FOR_FULFILLMENT:
            case client_lex_runtime_v2_1.IntentState.FULFILLED:
                callback(undefined, data);
                break;
            case client_lex_runtime_v2_1.IntentState.FAILED:
                callback(new Error('Bot conversation failed'));
                break;
            default:
                break;
        }
    }
    /**
     * Format UtteranceCommandOutput's response
     * decompress attributes
     * update audioStream format
     */
    async _formatUtteranceCommandOutput(data) {
        return {
            ...data,
            messages: await (0, utils_2.unGzipBase64AsJson)(data.messages),
            sessionState: await (0, utils_2.unGzipBase64AsJson)(data.sessionState),
            interpretations: await (0, utils_2.unGzipBase64AsJson)(data.interpretations),
            requestAttributes: await (0, utils_2.unGzipBase64AsJson)(data.requestAttributes),
            inputTranscript: await (0, utils_2.unGzipBase64AsJson)(data.inputTranscript),
            audioStream: data.audioStream
                ? await (0, utils_2.convert)(data.audioStream)
                : undefined,
        };
    }
    /**
     * handle client's `RecognizeTextCommand`
     * used for sending simple text message
     */
    async _handleRecognizeTextCommand(ctx, botConfig, data, baseParams, client) {
        logger.debug('postText to lex2', data);
        const params = {
            ...baseParams,
            text: data,
        };
        try {
            const recognizeTextCommand = new client_lex_runtime_v2_1.RecognizeTextCommand(params);
            const resultData = await client.send(recognizeTextCommand);
            this._reportBotStatus(ctx, resultData, botConfig);
            return resultData;
        }
        catch (err) {
            return Promise.reject(err);
        }
    }
    /**
     * handle client's `RecognizeUtteranceCommand`
     * used for obj text or obj voice message
     */
    async _handleRecognizeUtteranceCommand(ctx, botConfig, data, baseParams, client) {
        const { content, options: { messageType }, } = data;
        logger.debug('postContent to lex2', data);
        let params;
        // prepare params
        if (messageType === 'voice') {
            if (typeof content !== 'object') {
                return Promise.reject(new Error('invalid content type'));
            }
            const inputStream = content instanceof Uint8Array ? content : await (0, utils_2.convert)(content);
            params = {
                ...baseParams,
                requestContentType: 'audio/x-l16; sample-rate=16000; channel-count=1',
                inputStream,
            };
        }
        else {
            // text input
            if (typeof content !== 'string')
                return Promise.reject(new Error('invalid content type'));
            params = {
                ...baseParams,
                requestContentType: 'text/plain; charset=utf-8',
                inputStream: content,
            };
        }
        // make API call to lex
        try {
            const recognizeUtteranceCommand = new client_lex_runtime_v2_1.RecognizeUtteranceCommand(params);
            const resultData = await client.send(recognizeUtteranceCommand);
            const response = await this._formatUtteranceCommandOutput(resultData);
            this._reportBotStatus(ctx, response, botConfig);
            return response;
        }
        catch (err) {
            return Promise.reject(err);
        }
    }
}
exports.lexProvider = new AWSLexV2Provider();
